import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type {
	TikTokAdsCampaignDraft,
	TikTokAdsReallocationAction,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

const errorMessage = (error: unknown) =>
	error instanceof Error ? error.message : "Unknown error";

export const useTikTokAdsDrafts = () => {
	const { id: companyId } = useCompany();

	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.TikTokAds.Drafts(companyId),
		queryFn: () => Api.GetTikTokAdsDrafts().then((res) => res.data),
	});
};

export const useTikTokAdsDraft = (draftId: string) => {
	const { id: companyId } = useCompany();

	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.TikTokAds.Draft(companyId, draftId),
		queryFn: () => Api.GetTikTokAdsDraft(draftId).then((res) => res.data),
	});
};

export const useTikTokAdsIdentities = (enabled = true) =>
	useQuery({
		queryKey: ["TIKTOK_ADS", "IDENTITIES"],
		queryFn: () => Api.GetTikTokAdsIdentities().then((res) => res.data),
		enabled,
	});

export const useTikTokAdsRegions = (enabled = true) =>
	useQuery({
		queryKey: ["TIKTOK_ADS", "REGIONS"],
		queryFn: () => Api.GetTikTokAdsRegions().then((res) => res.data),
		enabled,
		staleTime: 60 * 60 * 1000,
	});

export const useTikTokAdsDraftActions = () => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const invalidateDrafts = () => {
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.TikTokAds.Drafts(companyId),
		});
		queryClient.invalidateQueries({
			queryKey: ["COMPANY", companyId, "INTEGRATIONS", "TIKTOK_ADS", "DRAFT"],
		});
	};

	const saveDraft = useMutation({
		mutationFn: (draft: TikTokAdsCampaignDraft) =>
			Api.SaveTikTokAdsDraft(draft).then((res) => res.data),
		onSuccess: () => {
			invalidateDrafts();
			emit({ type: AlertType.SUCCESS, content: "Draft saved." });
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to save draft: ${errorMessage(error)}`,
			});
		},
	});

	const deleteDraft = useMutation({
		mutationFn: (draftId: string) => Api.DeleteTikTokAdsDraft(draftId),
		onSuccess: () => {
			invalidateDrafts();
			emit({ type: AlertType.SUCCESS, content: "Draft deleted." });
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to delete draft: ${errorMessage(error)}`,
			});
		},
	});

	const publishDraft = useMutation({
		mutationFn: (draftId: string) =>
			Api.PublishTikTokAdsCampaign(draftId).then((res) => res.data),
		onSuccess: () => {
			invalidateDrafts();
			queryClient.invalidateQueries({
				queryKey: [
					"COMPANY",
					companyId,
					"INTEGRATIONS",
					"TIKTOK_ADS",
					"REPORT",
				],
			});
			emit({
				type: AlertType.SUCCESS,
				content:
					"Campaign created in TikTok, paused. Enable it when you're ready to spend.",
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to publish campaign: ${errorMessage(error)}`,
			});
		},
	});

	return { saveDraft, deleteDraft, publishDraft };
};

export const useTikTokAdsCampaignActions = (days: number) => {
	const { id: companyId } = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const invalidateReport = () =>
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.TikTokAds.Report(companyId, days),
		});

	const updateStatus = useMutation({
		mutationFn: (vars: { campaignId: string; status: "ENABLE" | "DISABLE" }) =>
			Api.UpdateTikTokAdsCampaignStatus(vars.campaignId, {
				status: vars.status,
			}),
		onSuccess: (_data, vars) => {
			invalidateReport();
			emit({
				type: AlertType.SUCCESS,
				content: `Campaign ${vars.status === "ENABLE" ? "activated" : "paused"}.`,
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to update campaign: ${errorMessage(error)}`,
			});
		},
	});

	const updateBudget = useMutation({
		mutationFn: (vars: { campaignId: string; budget: number }) =>
			Api.UpdateTikTokAdsCampaignBudget(vars.campaignId, {
				budget: vars.budget,
			}),
		onSuccess: () => {
			invalidateReport();
			emit({ type: AlertType.SUCCESS, content: "Campaign budget updated." });
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to update budget: ${errorMessage(error)}`,
			});
		},
	});

	const updateAdGroupStatus = useMutation({
		mutationFn: (vars: { adGroupId: string; status: "ENABLE" | "DISABLE" }) =>
			Api.UpdateTikTokAdsAdGroupStatus(vars.adGroupId, {
				status: vars.status,
			}),
		onSuccess: (_data, vars) => {
			invalidateReport();
			emit({
				type: AlertType.SUCCESS,
				content: `Ad group ${vars.status === "ENABLE" ? "activated" : "paused"}.`,
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to update ad group: ${errorMessage(error)}`,
			});
		},
	});

	const updateAdGroupBudget = useMutation({
		mutationFn: (vars: { adGroupId: string; budget: number }) =>
			Api.UpdateTikTokAdsAdGroupBudget(vars.adGroupId, {
				budget: vars.budget,
			}),
		onSuccess: () => {
			invalidateReport();
			emit({ type: AlertType.SUCCESS, content: "Ad group budget updated." });
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to update ad group budget: ${errorMessage(error)}`,
			});
		},
	});

	const applyReallocation = useMutation({
		mutationFn: (actions: TikTokAdsReallocationAction[]) =>
			Api.ApplyTikTokAdsReallocation({ actions }),
		onSuccess: () => {
			invalidateReport();
			emit({
				type: AlertType.SUCCESS,
				content: "Budget reallocation applied.",
			});
		},
		onError: (error) => {
			emit({
				type: AlertType.ERROR,
				content: `Failed to apply reallocation: ${errorMessage(error)}`,
			});
		},
	});

	return {
		updateStatus,
		updateBudget,
		updateAdGroupStatus,
		updateAdGroupBudget,
		applyReallocation,
	};
};

export const useTikTokAdsRecommendations = (enabled = false) =>
	useQuery({
		queryKey: ["TIKTOK_ADS", "RECOMMENDATIONS"],
		queryFn: () => Api.GetTikTokAdsRecommendations().then((res) => res.data),
		enabled,
	});
